/**
 * Ретеншн-офер (юзер 2026-07-04): одноразова спецпропозиція 449₴, яка
 * зʼявляється при ПЕРШОМУ вимкненні автопродовження. Живе 30 хвилин з
 * таймером обратного відліку.
 *
 * З 22.10.2026 (owner) офер приходить ПІСЛЯ вимкнення: «Все одно вимкнути»
 * вимикає одразу, а пропозиція показується станом «Автопродовження вимкнено,
 * але спецпропозиція ще діє». Закрити її = нічого не змінити.
 *
 * Тут:
 *  - <RetentionOfferBanner /> — закреп із таймером (стрічка / головна /
 *    підписка). Тап → відкриває попап-офер.
 *  - <RetentionOfferModals /> — глобальні модалки (в корені App):
 *      «Автопродовження вимкнено, але офер ще діє» (основний стан) і
 *      «Зачекай, є пропозиція» — лише коли автопродовження ще увімкнене.
 *      Відкриваються через store lib/retentionOffer.ts.
 *
 * Стан офера (available + expires_at) — з бекенду (metadata, серверний час).
 * Клієнт лише малює таймер від expires_at.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { ActionButton } from "@/components/ActionButton";
import {
  useCancelRecurring,
  useMySubscription,
  useRetentionOffer,
  useRetentionOfferGated,
  useRetentionOfferInvoice,
  type RetentionOfferOut,
} from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import {
  closeRetentionOffer,
  openRetentionOffer,
  subscribeRetentionOffer,
  type RetentionStep,
} from "@/lib/retentionOffer";

// ============================================================
// Countdown MM:SS від expires_at
// ============================================================

function useCountdown(expiresAt: string | null | undefined): {
  text: string;
  expired: boolean;
} {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return { text: "00:00", expired: true };
  const end = new Date(expiresAt).getTime();
  const ms = Math.max(0, end - now);
  const mm = Math.floor(ms / 60_000);
  const ss = Math.floor((ms % 60_000) / 1000);
  const text = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return { text, expired: ms <= 0 };
}

// ============================================================
// Анімований лічильник ціни: 990 → 449 при відкритті попапа (юзер 2026-07-04)
// ============================================================

function useCountUp(
  from: number,
  to: number,
  durationMs: number,
  enabled: boolean,
): number {
  const [val, setVal] = useState(enabled ? from : to);
  useEffect(() => {
    if (!enabled) {
      setVal(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — швидкий старт, плавне гальмування наприкінці.
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, durationMs, enabled]);
  return val;
}

// ============================================================
// Купівля офера — WFP invoice + pending_invoice для celebration-попапа
// ============================================================

async function openRetentionCheckout(
  invoiceMut: ReturnType<typeof useRetentionOfferInvoice>,
): Promise<void> {
  const webapp = tg();
  try {
    const result = await invoiceMut.mutateAsync();
    // pending_invoice → SubscriptionActivationWatcher покаже «Підписку
    // активовано» після повернення в аппку (той самий механізм, що тарифи).
    localStorage.setItem(
      "pending_invoice",
      JSON.stringify({
        order_reference: result.order_reference,
        tariff_slug: "retention449",
        started_at_before: Date.now(),
      }),
    );
    if (webapp?.openLink) webapp.openLink(result.purchase_url);
    else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    closeRetentionOffer();
  } catch (e) {
    webapp?.HapticFeedback?.notificationOccurred?.("error");
    showAppAlert(`Не вдалося створити рахунок: ${(e as Error).message}`);
  }
}

// ============================================================
// Закреп у стрічці / на головній / у підписці
// ============================================================

export function RetentionOfferBanner({
  wrapperClassName,
}: {
  /** Клас-обгортка (напр. "px-5 pt-3") — рендериться ЛИШЕ коли офер видимий,
   *  щоб не лишати порожній відступ на сторінці, коли офера немає. Якщо не
   *  задано — повертаємо саму картку (для контейнерів зі space-y). */
  wrapperClassName?: string;
}) {
  const q = useRetentionOfferGated();
  const offer = q.data;
  const cd = useCountdown(offer?.expires_at);
  if (!offer || !offer.available || cd.expired) return null;

  // Банер показує ТОЙ САМИЙ офер, що і попап (owner 2026-09-01: у стрічці
  // висів «Місяць за 449 ₴», хоча живий офер був «два місяці за 990»).
  const isStepTwoMonths = (offer.step ?? 1) === 1;

  const card = (
    <button
      type="button"
      onClick={() => {
        tg()?.HapticFeedback?.impactOccurred?.("medium");
        openRetentionOffer("offer");
      }}
      className="relative w-full overflow-hidden rounded-ios-card bg-surface p-4 text-left shadow-card border border-primary/25 active:scale-[0.99] transition"
    >
      {/* Делікатний теплий glow у кутку */}
      <div
        className="pointer-events-none absolute -top-8 -right-8 h-[6.875rem] w-[6.875rem] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,161,53,0.20), rgba(255,161,53,0))",
        }}
      />
      <div className="relative flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] bg-primary/10">
          <TgEmoji char="💎" size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.875rem] font-bold text-ink leading-tight">
            Спецпропозиція
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[0.75rem] text-ink-dim leading-snug">
            {isStepTwoMonths ? (
              <>
                <span>
                  Два місяці за{" "}
                  <b className="text-ink">{offer.two_months_price_uah} ₴</b>{" "}
                  <span className="line-through text-ink-faint">
                    {offer.original_price_uah *
                      Math.round(offer.two_months_days / 30)}{" "}
                    ₴
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 font-display text-[0.59375rem] font-bold uppercase leading-none text-white">
                  {discountLabel(
                    offer.original_price_uah *
                      Math.round(offer.two_months_days / 30),
                    offer.two_months_price_uah,
                  )}
                </span>
              </>
            ) : (
              <>
                <span>
                  Місяць за{" "}
                  <b className="text-ink">{offer.price_uah} ₴</b>{" "}
                  <span className="line-through text-ink-faint">
                    {offer.original_price_uah} ₴
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 font-display text-[0.59375rem] font-bold uppercase leading-none text-white">
                  {discountLabel(offer.original_price_uah, offer.price_uah)}
                </span>
              </>
            )}
          </div>
        </div>
        {/* Таймер — окрема «пілюля» shrink-0, щоб нічого не накладалось */}
        <div className="shrink-0 flex flex-col items-center rounded-[0.75rem] bg-primary/10 px-2.5 py-1.5">
          <span className="font-mono text-[0.5rem] uppercase tracking-wide text-primary/70 leading-none">
            залишилось
          </span>
          <span className="mt-0.5 font-display text-[0.9375rem] font-bold tabular-nums text-primary leading-none">
            {cd.text}
          </span>
        </div>
      </div>
    </button>
  );

  return wrapperClassName ? (
    <div className={wrapperClassName}>{card}</div>
  ) : (
    card
  );
}

// ============================================================
// Глобальні модалки — попап-2 (офер) + попап-3 (нагадування)
// ============================================================

export function RetentionOfferModals() {
  const [step, setStep] = useState<RetentionStep>(null);
  useEffect(() => subscribeRetentionOffer(setStep), []);
  // Офер і підписку тягнемо тільки коли модалка відкрита (щоб не робити
  // зайвих запитів), тому запити живуть у внутрішньому компоненті.
  if (step === null) return null;
  return <OpenRetentionModal step={step} />;
}

function OpenRetentionModal({
  step,
}: {
  step: Exclude<RetentionStep, null>;
}) {
  const q = useRetentionOffer(true);
  const offer = q.data;
  const subQ = useMySubscription();

  if (step === "offer") {
    // Банер і кнопка з нагадування в боті відкривають «offer». Офер тепер
    // запускається лише ПІСЛЯ вимкнення (owner 2026-10-22), тож зазвичай
    // автопродовження вже вимкнене — і показуємо чесний стан «вимкнено, але
    // пропозиція ще діє». «Зачекай, є пропозиція» лишається для випадку,
    // коли автопродовження таки увімкнене.
    if (subQ.isPending) return null;
    const recurringOff = !!(
      subQ.data &&
      !(isActiveSubscription(subQ.data) && subQ.data.is_recurring)
    );
    if (!recurringOff) return <OfferModal offer={offer} />;
  }
  return <ReminderModal offer={offer} />;
}

function ModalShell({
  children,
  onClose = closeRetentionOffer,
}: {
  children: React.ReactNode;
  onClose?: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function CloseX({ onClose = closeRetentionOffer }: { onClose?: () => void }) {
  // Єдиний стиль хрестика (owner 2026-07-10) — через спільний PopupCloseButton.
  return <PopupCloseButton onClose={onClose} />;
}

/** Блок ціни: 990 закреслено → 449 велика + бейдж знижки.
 *  animate=true → велика цифра анімовано «стікає» 990 → 449 (~3.4с). */
/** Ціна кроку 1: 990 ₴ за 60 днів. Велика цифра анімовано «стікає»
 *  1980 → 990 (owner 2026-07-29: «зроби так само, як у другому офері»). */
/** Бейдж знижки: «Знижка 50%». Рахуємо від показаних цін, а не хардкодимо —
 *  у «двох місяцях» це 50% (990 з 1980), у місяці зі знижкою — 55%
 *  (owner 2026-09-01: «писать не просто проценты, а и слово скидка»). */
function discountLabel(full: number, price: number): string {
  const pct = Math.round((1 - price / full) * 100);
  return `Знижка ${pct}%`;
}

function TwoMonthsPrice({
  offer,
  animate = false,
}: {
  offer: RetentionOfferOut;
  animate?: boolean;
}) {
  const full = offer.original_price_uah * Math.round(offer.two_months_days / 30);
  const shown = useCountUp(full, offer.two_months_price_uah, 3400, animate);
  return (
    <div className="flex items-center justify-center gap-3 my-4">
      <div
        className="font-display text-[2.5rem] font-bold leading-none text-primary tabular-nums"
        style={{ letterSpacing: "-0.02em" }}
      >
        {shown} ₴
      </div>
      <div className="flex flex-col items-start gap-1">
        <span className="font-mono text-[0.8125rem] text-ink-dim line-through tabular-nums leading-none">
          {full} ₴
        </span>
        <span className="rounded-full bg-primary px-2 py-0.5 font-display text-[0.625rem] font-bold uppercase leading-none text-white">
          {discountLabel(full, offer.two_months_price_uah)}
        </span>
      </div>
    </div>
  );
}

function PriceRow({
  offer,
  animate = false,
}: {
  offer: RetentionOfferOut;
  animate?: boolean;
}) {
  const shown = useCountUp(
    offer.original_price_uah,
    offer.price_uah,
    3400,
    animate,
  );
  return (
    <div className="flex items-center justify-center gap-3 my-4">
      <div className="font-display text-[2.5rem] font-bold leading-none text-primary tabular-nums" style={{ letterSpacing: "-0.02em" }}>
        {shown} ₴
      </div>
      <div className="flex flex-col items-start gap-1">
        <span className="font-mono text-[0.8125rem] text-ink-dim line-through tabular-nums leading-none">
          {offer.original_price_uah} ₴
        </span>
        <span className="rounded-full bg-primary px-2 py-0.5 font-display text-[0.625rem] font-bold uppercase leading-none text-white">
          {discountLabel(offer.original_price_uah, offer.price_uah)}
        </span>
      </div>
    </div>
  );
}

export function OfferModal({
  offer,
  onClose = closeRetentionOffer,
  preview = false,
}: {
  offer: RetentionOfferOut | undefined;
  onClose?: () => void;
  /** preview=true (галерея адміна): жодних реальних мутацій — усі кнопки
   *  лише закривають попап; таймер не автозакриває. */
  preview?: boolean;
}) {
  const cd = useCountdown(offer?.expires_at);
  const invoiceMut = useRetentionOfferInvoice();
  const cancelMut = useCancelRecurring();
  const subQ = useMySubscription();
  // Чи ввімкнено зараз автопродовження. Якщо юзер уже вимкнув його раніше —
  // кнопки «Вимкнути автопродовження» бути не повинно (нічого вимикати) —
  // показуємо «Не зараз» (юзер 2026-07-04). У preview показуємо повний
  // вигляд (з кнопкою вимкнення).
  const isRecurring =
    preview ||
    !!(
      subQ.data &&
      isActiveSubscription(subQ.data) &&
      subQ.data.is_recurring
    );

  // Час вийшов, поки попап відкритий → закриваємо (офер згорів).
  useEffect(() => {
    if (!preview && offer && cd.expired) closeRetentionOffer();
  }, [offer, cd.expired, preview]);

  if (!offer) return null;

  const isStepTwoMonths = (offer.step ?? 1) === 1;

  const handleDisable = async () => {
    if (preview) {
      onClose();
      return;
    }
    const webapp = tg();
    try {
      await cancelMut.mutateAsync();
      webapp?.HapticFeedback?.notificationOccurred?.("success");
      openRetentionOffer("reminder");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Не вдалося вимкнути: ${(e as Error).message}`);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <CloseX onClose={onClose} />
      <div className="text-center">
        {/* Плашка-пояснення (owner 2026-07-29): людина щойно натиснула
            «вимкнути» і має розуміти, що автопродовження ЩЕ НЕ вимкнено —
            спершу є що запропонувати. Текст один на обидва офери: після
            переходу на драбину (owner 2026-08-31) знижка 55% показується
            ПЕРШОЮ, і «А так? Остання пропозиція» читалось так, ніби людина
            вже від чогось відмовилась. */}
        {/* Лише поки автопродовження справді увімкнене (owner 2026-10-22):
            після вимкнення «зачекай» читалось би як «ще не вимкнено». */}
        {isRecurring ? (
          <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full bg-ink/[0.06] px-3 py-1">
            <span className="font-display text-[0.71875rem] font-semibold leading-none text-ink-dim [text-wrap:balance]">
              Зачекай, є пропозиція
            </span>
          </div>
        ) : null}
        <div className="mb-2 flex justify-center">
          <TgEmoji char="💎" size={48} />
        </div>
        {/* Таймер-плашка урджентності */}
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 mb-3">
          <TgEmoji char="⏳" size={14} />
          <span className="font-display text-[0.8125rem] font-bold tabular-nums text-primary leading-none">
            {cd.text}
          </span>
        </div>
        <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight [text-wrap:balance]">
          {isStepTwoMonths ? "Два місяці за ціною одного" : "Найкраща пропозиція"}
        </div>
        <div className="text-[0.8125rem] text-ink-dim mt-2 leading-relaxed [text-wrap:balance]">
          Ми хочемо, щоб ти залишився з нами. У inFLOW немає знижок та акцій —
          тому цю пропозицію можна використати лише зараз, іншого шансу не
          буде.
        </div>
      </div>

      {isStepTwoMonths ? (
        <TwoMonthsPrice offer={offer} animate />
      ) : (
        <PriceRow offer={offer} animate />
      )}

      <div className="rounded-2xl bg-ink/[0.035] border border-ink/8 p-3 mb-4 text-center">
        <div className="text-[0.75rem] text-ink-dim leading-snug [text-wrap:balance]">
          {isStepTwoMonths ? (
            <>
              <b className="text-ink">{offer.two_months_days} днів</b> повного
              доступу за {offer.two_months_price_uah} ₴ — це{" "}
              {Math.round(offer.two_months_price_uah / (offer.two_months_days / 30))} ₴
              за місяць.
            </>
          ) : (
            <>
              Місяць повного доступу за{" "}
              <b className="text-ink">{offer.price_uah} ₴</b>.
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <ActionButton
          onClick={() =>
            preview ? onClose() : openRetentionCheckout(invoiceMut)
          }
          disabled={invoiceMut.isPending}
        >
          {invoiceMut.isPending
            ? "Створюю рахунок…"
            : `Оформити за ${
                isStepTwoMonths ? offer.two_months_price_uah : offer.price_uah
              } грн`}
        </ActionButton>
        {isRecurring ? (
          <ActionButton
            tone="secondary"
            onClick={handleDisable}
            disabled={cancelMut.isPending}
          >
            {cancelMut.isPending ? "Вимикаю…" : "Вимкнути автопродовження"}
          </ActionButton>
        ) : null}
      </div>
    </ModalShell>
  );
}

export function ReminderModal({
  offer,
  onClose = closeRetentionOffer,
  preview = false,
}: {
  offer: RetentionOfferOut | undefined;
  onClose?: () => void;
  preview?: boolean;
}) {
  const cd = useCountdown(offer?.expires_at);
  const invoiceMut = useRetentionOfferInvoice();

  useEffect(() => {
    if (!preview && offer && cd.expired) closeRetentionOffer();
  }, [offer, cd.expired, preview]);

  if (!offer) return null;

  // Текст — за кроком офера (owner 2026-09-01: вимикав «два місяці», а після
  // вимкнення отримав 449). Рахунок сервер виставляє за кроком, тож
  // розбіжність була саме в тексті — кнопка обіцяла 449, а рахунок на 990.
  // З 22.10.2026 це основний попап офера: людина бачить його одразу після
  // «Все одно вимкнути», вже з вимкненим автопродовженням.
  const isStepTwoMonths = (offer.step ?? 1) === 1;

  return (
    <ModalShell onClose={onClose}>
      <CloseX onClose={onClose} />
      <div className="text-center">
        <div className="mb-2 flex justify-center">
          <TgEmoji char="⏳" size={48} />
        </div>
        <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight">
          Автопродовження вимкнено
        </div>
        <div className="text-[0.8125rem] text-ink-dim mt-2 leading-relaxed [text-wrap:balance]">
          {isStepTwoMonths ? (
            <>
              Але спецпропозиція ще діє — два місяці повного доступу за{" "}
              <b className="text-ink">{offer.two_months_price_uah} ₴</b>.
              Залишилось <b className="text-primary tabular-nums">{cd.text}</b>.
            </>
          ) : (
            <>
              Але спецпропозиція ще діє — місяць повного доступу за{" "}
              <b className="text-ink">{offer.price_uah} ₴</b> (−
              {offer.discount_percent}%). Залишилось{" "}
              <b className="text-primary tabular-nums">{cd.text}</b>.
            </>
          )}
        </div>
      </div>

      {/* Ціна «стікає» 990 → 449: тепер людина бачить офер уперше саме тут. */}
      {isStepTwoMonths ? (
        <TwoMonthsPrice offer={offer} animate />
      ) : (
        <PriceRow offer={offer} animate />
      )}

      <div className="space-y-2">
        <ActionButton
          onClick={() =>
            preview ? onClose() : openRetentionCheckout(invoiceMut)
          }
          disabled={invoiceMut.isPending}
        >
          {invoiceMut.isPending
            ? "Створюю рахунок…"
            : `Оформити за ${
                isStepTwoMonths
                  ? offer.two_months_price_uah
                  : offer.price_uah
              } грн`}
        </ActionButton>
      </div>
    </ModalShell>
  );
}
